import type { AccessStatus } from "./types";
import { LandingDiagram } from "./LandingDiagram";

interface LandingPageProps {
  status: AccessStatus | "signed-out";
  busy: boolean;
  error: string | null;
  onSignIn: () => void;
  onSignOut: () => void;
}

const statusCopy = {
  pending: {
    eyebrow: "Request received",
    title: "Your workspace is almost ready.",
    body: "The workspace maintainer needs to approve your account. You can close this page and return later.",
  },
  denied: {
    eyebrow: "Access not approved",
    title: "This account cannot enter yet.",
    body: "Contact the workspace maintainer if you believe this decision should be reviewed.",
  },
} as const;

export function LandingPage({ status, busy, error, onSignIn, onSignOut }: LandingPageProps) {
  const accessCopy = status === "pending" || status === "denied" ? statusCopy[status] : null;

  return (
    <main className="landing-shell" data-app-ready="true">
      <section className="landing-intro" aria-labelledby="landing-title">
        <div className="landing-brand"><span aria-hidden="true">M</span><strong>Mermaid Designer</strong></div>
        <div className="landing-copy">
          <p className="landing-eyebrow">Private visual studio</p>
          <h1 id="landing-title">{accessCopy?.title ?? "A visual workspace where structure stays yours."}</h1>
          <p className="landing-lede">{accessCopy?.body ?? "Write and visualize with Mermaid source, then think freely with a flexible canvas."}</p>
          {accessCopy ? <p className="landing-state-label">{accessCopy.eyebrow}</p> : (
            <p className="landing-secondary">Code-aware diagrams and free-form exploration, together in one private space.</p>
          )}
          {status === "signed-out" ? (
            <button className="google-sign-in" type="button" disabled={busy} onClick={onSignIn}>
              <span className="google-mark" aria-hidden="true">G</span>
              {busy ? "Opening Google…" : "Continue with Google"}
            </button>
          ) : <button className="secondary-auth-action" type="button" disabled={busy} onClick={onSignOut}>Use another account</button>}
          {error ? <p className="auth-error" role="alert">{error}</p> : null}
          <p className="approval-note">New accounts wait for maintainer approval.</p>
          <ol className="access-steps" aria-label="Access steps">
            <li><span>1</span><div><strong>Sign in</strong><small>Use your Google account.</small></div></li>
            <li><span>2</span><div><strong>Request access</strong><small>New accounts are reviewed.</small></div></li>
            <li><span>3</span><div><strong>Enter workspace</strong><small>Continue in your private studio.</small></div></li>
          </ol>
        </div>
        <p className="landing-footnote">Your work stays private. No AI provider required.</p>
      </section>
      <section className="landing-preview" aria-label="Product preview"><LandingDiagram /></section>
    </main>
  );
}
